import {z} from 'zod'

import {ProductType} from '../enum/productType'

/* Generic Error Response */

/**
 * Generic wrapper to respond on failed API call.
 */
export interface FailedResponse {
  succeeded: false
  errStatusCode: number
  errorKey?: string
}

/**
 * API error response schema (for errors with body).
 */
export const ErrorResponseSchema = z.object({
  key: z.string(),
  message: z.string()
})

/* Admin Products */

export interface AdminProductResponseItem {
  id: string
  productType: ProductType
  description: string
  priceCents: number
}

export const AdminProductResponseItemSchema = z.object({
  id: z.string(),
  productType: z.nativeEnum(ProductType),
  description: z.string(),
  priceCents: z.number()
})

export const AdminProductsResponseSchema = z.array(AdminProductResponseItemSchema)

/* Admin Users */

export interface AdminUserResponseItem {
  id: string
  email: string
  auth0Id: string
  useAuthorized: boolean
  isAdmin: boolean
  createdAt?: string
  updatedAt?: string
}

export const AdminUserResponseItemSchema = z.object({
  id: z.string(),
  email: z.string(),
  auth0Id: z.string(),
  useAuthorized: z.boolean().optional().default(false),
  isAdmin: z.boolean().optional().default(false),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
})

export const AdminUsersResponseSchema = z.array(AdminUserResponseItemSchema)

/* Auth Claims */

export interface ClaimsResponse {
  email: string
  isAuthorized: boolean
  isAdmin: boolean
}

export const ClaimsResponseSchema = z.object({
  email: z.string().optional().default(''),
  isAuthorized: z.boolean(),
  isAdmin: z.boolean()
})

/* Generate Product List */

export interface GenerateProductListVariant {
  entries: GenerateProductListResponseItem[]
  totalCost: number
}

export interface GenerateProductListResponse {
  inductive: GenerateProductListVariant
  ultrasonic: GenerateProductListVariant | null
}

export interface GenerateProductListResponseItem {
  description: string
  quantity: number
}

export const GenerateProductListResponseItemSchema = z.object({
  description: z.string(),
  quantity: z.number().min(1)
})

export const GenerateProductListVariantSchema = z.object({
  entries: z.array(GenerateProductListResponseItemSchema),
  totalCost: z.number().min(0)
})

export const GenerateProductListResponseSchema = z.object({
  inductive: GenerateProductListVariantSchema,
  ultrasonic: GenerateProductListVariantSchema.nullable()
})

/* Generate SVG Drawing */

export interface GenerateSvgDrawingResponse {
  svg: string
}

export const GenerateSvgDrawingResponseSchema = z.object({
  svg: z.string()
})

/* Service Prices */

/**
 * Service prices response.
 * Note: using string fields for decimal values as they are not used in arithmetic operations (and probably should not
 * be in the frontend code).
 */
export interface ServicePricesResponse {
  hydrolinkApiPrice: number
  hydrolinkPremiumPrice: number
  vatMultiplier: number
}

export const ServicePricesResponseSchema = z.object({
  hydrolinkApiPrice: z.number(),
  hydrolinkPremiumPrice: z.number(),
  vatMultiplier: z.number()
})
